/** Team hooks with mock fallback (same contract as prior phases). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TEAM_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { TeamMember } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { teamApi } from "./team.api";
import { toTeamMember, toTeamWriteBody, toMockTeamMember, type TeamMemberInput } from "./team.adapter";
import { useTeamStore } from "./team.store";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseTeamMembersResult {
  members: TeamMember[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useTeamMembers(): UseTeamMembersResult {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useTeamStore((s) => s.active);

  const query = useQuery({
    queryKey: queryKeys.teamList(LIST_PARAMS),
    queryFn: () => teamApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (!authed) return { members: mockActive, isLoading: false, isError: false, isFetching: false, source: "mock", error: null, refetch: query.refetch };
  if (query.data) return { members: query.data.data.map(toTeamMember), isLoading: false, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
  if (query.isError) return { members: mockActive, isLoading: false, isError: true, isFetching: false, source: "mock", error: query.error, refetch: query.refetch };
  return { members: [], isLoading: query.isLoading, isError: false, isFetching: query.isFetching, source: "api", error: null, refetch: query.refetch };
}

/** No dedicated `/team/:id` endpoint — derive a single member from the list. */
export function useTeamMember(id: string | undefined): { member: TeamMember | undefined; isLoading: boolean; isError: boolean; source: DataSource } {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useTeamStore((s) => s.active);
  const query = useQuery({
    queryKey: queryKeys.teamDetail(id ?? ""),
    queryFn: () => teamApi.get(id as string),
    enabled: authed && !!id,
  });
  const mockMember = id ? mockActive.find((m) => m.id === id) : undefined;

  if (!authed) return { member: mockMember, isLoading: false, isError: false, source: "mock" };
  if (query.data) return { member: toTeamMember(query.data), isLoading: false, isError: false, source: "api" };
  if (query.isError) return { member: mockMember, isLoading: false, isError: true, source: "mock" };
  return { member: undefined, isLoading: query.isLoading, isError: false, source: "api" };
}

export function useMyTeamProfile(): { member: TeamMember | undefined; isLoading: boolean; isError: boolean; source: DataSource; refetch: () => void } {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const mockActive = useTeamStore((s) => s.active);
  const query = useQuery({
    queryKey: queryKeys.teamMe,
    queryFn: () => teamApi.me(),
    enabled: authed,
  });

  if (!authed) return { member: mockActive[0], isLoading: false, isError: false, source: "mock", refetch: query.refetch };
  if (query.data) return { member: toTeamMember(query.data), isLoading: false, isError: false, source: "api", refetch: query.refetch };
  if (query.isError) return { member: undefined, isLoading: false, isError: true, source: "api", refetch: query.refetch };
  return { member: undefined, isLoading: query.isLoading, isError: false, source: "api", refetch: query.refetch };
}

function useInvalidateTeam() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: TEAM_QUERY_PREFIX });
}

export function useCreateTeamMember() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const add = useTeamStore((s) => s.add);
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: async (input: TeamMemberInput) => {
      if (authed) return teamApi.create(toTeamWriteBody(input));
      const mock = toMockTeamMember(input);
      add(mock);
      return mock;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useUpdateTeamMember() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const update = useTeamStore((s) => s.update);
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: TeamMemberInput }) => {
      if (authed) return teamApi.update(id, toTeamWriteBody(input));
      update(id, { name: input.name, email: input.email, role: input.role, salary: input.salary });
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useArchiveTeamMember() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const archive = useTeamStore((s) => s.archive);
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return teamApi.archive(id);
      archive(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}

export function useRestoreTeamMember() {
  const authed = useAuthStore((s) => s.status === "authenticated");
  const restore = useTeamStore((s) => s.restore);
  const invalidate = useInvalidateTeam();
  return useMutation({
    mutationFn: async (id: string) => {
      if (authed) return teamApi.restore(id);
      restore(id);
      return null;
    },
    onSuccess: () => {
      if (authed) void invalidate();
    },
  });
}
